import React from 'react';

import { keyedList } from 'utils';

export function formatNumber(num, decimal, separator, trim) {
  let str = (typeof num === 'string' ? num : num.toFixed(decimal || 0));
  if (separator === undefined) {
    return str;
  }
  if (trim) str = parseFloat(str).toString();
  const parts = str.split(".");
  if (parseFloat(num) >= separator) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return parts.join(".");
}

export function formatDamage(val) {
  if (val > 1e+13) {
    return formatNumber(val / 1e+12, 3, 10000) + " T";
  } else if (val > 1e+10) {
    return formatNumber(val / 1e+9, 3, 10000) + " B";
  } else if (val > 1e+7) {
    return formatNumber(val / 1e+6, 3, 10000) + " M";
  } else {
    return formatNumber(val, 0, 10000);
  }
}

/**
 * Replaces {0} tags in string with provided arguments
 * 
 * @param {string} str format string
 * @param {...string|function} args substitutions or callback function
 * @returns {string} formatted string
 */
export function format(str, ...args) {
  const getter = (typeof args[0] === "function" ? args[0] : ((idx, m) => idx in args ? args[idx] : m));
  return str.replace(/{(\d+)(?::(.*?))?}(?:\|plural\((.*?)\))?/g, (m, idx, options, plural) => {
    const value = getter(parseInt(idx), m);
    if (plural) {
      const args = plural.split(',');
      return parseInt(value) > 1 ? args[1] : args[0];
    } else if (options) {
      return options.split('|')[parseInt(value) - 1];
    }
    return value;
  });
}

/**
 * Replaces {0} tags in string with provided arguments, returns results as an array
 * 
 * @param {string} str format string
 * @param {...string|function} args substitutions or callback function
 * @returns {any[]} formatted string pieces
 */
export function formatReact(str, ...args) {
  const getter = (typeof args[0] === "function" ? args[0] : (idx => args[idx]));
  return keyedList(str.split(/({\d+(?::.*?)?}(?:\|plural\(.*?\))?)/).map(piece => {
    const m = piece.match(/^{(\d+)(?::(.*?))?}(?:\|plural\((.*)\))?$/);
    if (!m) return piece;
    const value = getter(parseInt(m[1]));
    if (m[3]) {
      const args = m[3].split(',');
      return parseInt(value) > 1 ? args[1] : args[0];
    } else if (m[2]) {
      return m[2].split('|')[parseInt(value) - 1];
    }
    return value;
  }));
}

export function _R(strings, ...args) {
  let src = strings;
  if (Array.isArray(strings)) {
    src = strings.map((str, index) => (index ? `{${index - 1}}` : "") + str.replace(/[{}]/g, "\\$&")).join("");
  }
  return formatReact(src, ...args);
}

export function formatDescription(text) {
  const colors = {
    magic: "blue",
    rare: "yellow",
    epic: "purple",
    legendary: "orange",
  };
  return text.split(/({c_\w+}.*?{\/c}|\n)/).map((piece, idx) => {
    const m = piece.match(/^{c_(\w+)}(.*){\/c}$/);
    if (m) {
      return <span key={idx} className={`d2-color-${colors[m[1]] || m[1]}`}>{m[2]}</span>;
    } else if (piece === "\n") {
      return <br key={idx}/>;
    } else {
      return piece;
    }
  });
}

export function replaceReact(text, regex, func, textFunc) {
  const result = [];
  function add(element) {
    if (React.isValidElement(element)) {
      result.push(React.cloneElement(element, {key: result.length}));
    } else if (element != null) {
      result.push(element);
    }
  }
  textFunc = textFunc || (text => text);
  let prev = 0, match;
  while ((match = regex.exec(text))) {
    if (match.index > prev) {
      result.push(textFunc(text.substring(prev, match.index)));
    }
    const element = func(...match, match.index, match.string);
    if (Array.isArray(element)) {
      for (const e of element) {
        add(e);
      }
    } else {
      add(element);
    }
    prev = match.index + match[0].length;
    if (!regex.global && !regex.sticky) break;
  }
  if (prev < text.length) {
    result.push(textFunc(text.substring(prev)));
  }
  return result;
}

export function replaceHtml(text, Element, textFunc) {
  const stack = [{children: []}];
  function pop() {
    const top = stack.pop();
    const elem = <Element tag={top.tag} attributes={top.attributes} children={keyedList(top.children)}/>;
    stack[stack.length - 1].children.push(elem);
  }
  for (const part of text.split(/(<[^>]*>)/)) {
    let m;
    if ((m = part.match(/^<(\w+)(?:\s(.*))?(@.*)?>$/))) {
      const entry = {tag: m[1].toLowerCase(), attributes: {}, children: []};
      for (const [, key, value] of (m[2]?.matchAll(/(\w+)\s*=\s*['"](.*?)['"]/g) || [])) {
        entry.attributes[key.toLowerCase()] = value;
      }
      if (m[3]) entry.attributes.argument = m[3].substring(1);
      stack.push(entry);
      if (entry.tag === 'br' || m[3]) {
        pop();
      }
    } else if ((m = part.match(/^<\/(\w+)\s*>$/))) {
      const tag = m[1].toLowerCase();
      while (stack.length > 1 && stack[stack.length - 1].tag !== tag) {
        pop();
      }
      if (stack.length > 1) {
        pop();
      }
    } else if (part) {
      stack[stack.length - 1].children.push(textFunc ? textFunc(part) : part);
    }
  }
  while (stack.length > 1) {
    pop();
  }
  return keyedList(stack[0].children);
}

export function formatHtml(text, ...args) {
  if (!text) return null;
  function replaceArgs(text) {
    return formatReact(text, ...args);
  }
  function replaceBreaks(text) {
    return replaceReact(text, /\n|&nbsp;/g, txt => {
      if (txt === '\n') return <br/>;
      if (txt === '&nbsp;') return '\u00a0';
    }, replaceArgs);
  }

  text = text.replace(/{(\d+):(.*?)}(%?)/g, (m, idx, options, pct) => {
    const value = args[parseInt(idx)];
    const opts = options.split('|');
    if (Array.isArray(value) && opts.every(o => !isNaN(o))) {
      let result = `\u2060<FONT COLOR='#999999'>[`;
      opts.forEach((o, i) => {
        if (i) result += '/\u200B';
        if (value.includes(i + 1)) {
          result += `</FONT>${o}${pct}<FONT COLOR='#999999'>`;
        } else {
          result += o + pct;
        }
      });
      result += `]</FONT>`;
      return result;
    } else {
      return options.split('|')[parseInt(value) - 1] + pct;
    }
  });
  text = text.replace(/&nbsp;/g, '\u00A0');

  function TagElement({ tag, attributes, children }) {
    if (tag === 'font') {
      const attr = {};
      //if (attributes.size) attr.fontSize = `${parseFloat(format(attributes.size, ...args)) / 14}em`;
      if (attributes.color) {
        const color = format(attributes.color, ...args);
        attr.color = color.replace(/#\w{2}(\w{6})/, (m, c) => `#${c}`);
      }
      return <span style={attr}>{children}</span>;
    } else if (tag === 'br') {
      return <br/>;
    // } else if (tag === 'textformat') {
    //   const style = {};
    //   if (attributes.indent) style.textIndent = parseInt(attributes.indent);
    //   if (attributes.leftmargin) style.marginLeft = parseInt(attributes.leftmargin);
    //   return <span style={style}>{children}</span>;
    } else if (tag === 'img') {
      return <span className={`la-img-${attributes.src}`}/>;
    } else if (tag === 'symbol_positive') {
      return <span className={`la-img-emoticon_sign_greenDot`}/>;
    } else {
      return children;
    }
  }
  return replaceHtml(text, TagElement, replaceBreaks);
}

export function removeBreaks(text) {
  if (!text) return null;
  return text.replace(/\b\n\b/g, ' ').replace(/\n/g, '');
}
